/**
 * PEARL ANN BROWN — single source of truth for all copy.
 *
 * RULE: every string below comes from the existing site. Nothing has been
 * invented. The only substitution made is the brand name: every occurrence of
 * the former "Dermalipo" now reads "Pearl Ann Brown", taken from the practice
 * email already published on the site (contact@pearlannbrown.com).
 *
 * Where the original site had no data (phone number, physician license number,
 * NPI, prices, per-day hours) the field is simply absent — it is NOT filled
 * with a placeholder, because a wrong value is worse than none.
 */

export type Language = 'en' | 'es'

/** Facts that appear on the site today. Identical in both languages. */
export const PRACTICE = {
  /** Wordmark, letter-spaced uppercase. */
  name: 'PEARL ANN BROWN',
  /** Same brand in sentence case, for running text. */
  nameTitle: 'Pearl Ann Brown',
  address: '9 East 62nd St, New York, NY 10065',
  addressLines: ['9 East 62nd St', 'New York, NY 10065'],
  email: 'contact@pearlannbrown.com',
  state: 'New York',
  copyrightYear: '2026',
  heroImage:
    'https://cdn.magicpatterns.com/uploads/sUFK5tKBD2PKf3yJhJKKqK/Screenshot_2026-04-09_at_1.49.08_PM.png',
} as const

/** Small bronze line under the wordmark. */
export const BRAND_TAGLINE = {
  en: 'Plastic Surgery · New York',
  es: 'Cirugía Plástica · Nueva York',
} as const

export const NAV = {
  en: {
    links: [
      { to: '/about', label: 'The Practice' },
      { to: '/services', label: 'Services' },
      { to: '/process', label: 'Process' },
      { to: '/faq', label: 'FAQ' },
      { to: '/contact', label: 'Contact' },
    ],
    cta: 'Book a Consultation',
    menu: 'Menu',
    close: 'Close',
  },
  es: {
    links: [
      { to: '/about', label: 'La Práctica' },
      { to: '/services', label: 'Servicios' },
      { to: '/process', label: 'Proceso' },
      { to: '/faq', label: 'Preguntas' },
      { to: '/contact', label: 'Contacto' },
    ],
    cta: 'Reservar Consulta',
    menu: 'Menú',
    close: 'Cerrar',
  },
} as const

export const HOME = {
  en: {
    hero: {
      eyebrow: 'Plastic Surgery & Aesthetic Medicine · New York',
      headline: 'Private Medical Care Tailored to You',
      sub: 'Plastic surgery & aesthetic procedures by licensed physicians. Personalized consultations. Complete discretion.',
      cta: 'Book a Consultation',
      ctaSecondary: 'View Services',
      badges: [
        'Plastic Surgery',
        'Licensed',
        'English & Spanish',
        'By Appointment Only',
      ],
      imageCaption: 'Private Consultation Room',
    },
    trustStrip: [
      { label: 'Licensed', detail: 'New York State' },
      { label: 'HIPAA', detail: 'Patient confidentiality' },
      { label: 'Bilingual', detail: 'English & Spanish' },
      { label: 'Private', detail: 'By appointment only' },
    ],
    aboutTeaser: {
      eyebrow: 'The Practice',
      headline: 'A Practice Built on Trust',
      lead: 'Pearl Ann Brown is a professional plastic surgery and aesthetic medicine practice operated by licensed physicians in New York State.',
      link: 'Read more about the practice',
    },
    servicesTeaser: {
      eyebrow: 'Services',
      headline: 'Surgical & Aesthetic Services',
      sub: 'Comprehensive plastic surgery and aesthetic procedures tailored to your goals.',
      link: 'See all services',
    },
    processTeaser: {
      eyebrow: 'How It Works',
      headline: 'From request to private consultation',
      link: 'See the full process',
    },
    faqTeaser: {
      eyebrow: 'Questions',
      headline: 'Frequently Asked Questions',
      link: 'Read all questions',
    },
    contactTeaser: {
      eyebrow: 'Contact',
      headline: 'Request a private consultation',
      sub: 'A coordinator will contact you within 24 hours to schedule.',
      cta: 'Book a Consultation',
    },
  },
  es: {
    hero: {
      eyebrow: 'Cirugía Plástica y Medicina Estética · Nueva York',
      headline: 'Atención Médica Privada Hecha a Su Medida',
      sub: 'Cirugía plástica y procedimientos estéticos por médicos con licencia. Consultas personalizadas. Discreción total.',
      cta: 'Reservar Consulta',
      ctaSecondary: 'Ver Servicios',
      badges: [
        'Cirugía Plástica',
        'Licenciado',
        'Inglés y Español',
        'Solo con Cita',
      ],
      imageCaption: 'Sala de Consulta Privada',
    },
    trustStrip: [
      { label: 'Licenciado', detail: 'Estado de Nueva York' },
      { label: 'HIPAA', detail: 'Confidencialidad del paciente' },
      { label: 'Bilingüe', detail: 'Inglés y español' },
      { label: 'Privado', detail: 'Solo con cita previa' },
    ],
    aboutTeaser: {
      eyebrow: 'La Práctica',
      headline: 'Una Práctica Basada en la Confianza',
      lead: 'Pearl Ann Brown es una práctica profesional de cirugía plástica y medicina estética operada por médicos con licencia en el Estado de Nueva York.',
      link: 'Conocer más sobre la práctica',
    },
    servicesTeaser: {
      eyebrow: 'Servicios',
      headline: 'Servicios Quirúrgicos y Estéticos',
      sub: 'Cirugía plástica integral y procedimientos estéticos adaptados a sus objetivos.',
      link: 'Ver todos los servicios',
    },
    processTeaser: {
      eyebrow: 'Cómo Funciona',
      headline: 'De la solicitud a la consulta privada',
      link: 'Ver el proceso completo',
    },
    faqTeaser: {
      eyebrow: 'Preguntas',
      headline: 'Preguntas Frecuentes',
      link: 'Leer todas las preguntas',
    },
    contactTeaser: {
      eyebrow: 'Contacto',
      headline: 'Solicite una consulta privada',
      sub: 'Un coordinador se comunicará con usted en 24 horas para programar.',
      cta: 'Reservar Consulta',
    },
  },
} as const

export const ABOUT = {
  en: {
    eyebrow: 'The Practice',
    headline: 'A Practice Built on Trust',
    body: 'Pearl Ann Brown is a professional plastic surgery and aesthetic medicine practice operated by licensed physicians in New York State. We specialize in surgical body contouring, facial rejuvenation, and non-surgical aesthetic treatments. Our practice provides personalized, confidential consultations in a private setting, adhering to all state and federal regulations governing medical care and patient privacy.',
    credentials: [
      {
        icon: 'shield',
        title: 'Licensed',
        description: 'Fully licensed to practice medicine in New York State',
      },
      {
        icon: 'lock',
        title: 'Patient Confidentiality',
        description: 'HIPAA-compliant with complete discretion guaranteed',
      },
    ],
    statementTitle: 'Professional Medical Practice',
    statement:
      'Pearl Ann Brown operates as a professional medical practice under the laws of New York State. All physicians are licensed to practice medicine in New York. Our practice complies with all applicable state and federal regulations, including HIPAA privacy requirements. Medical services are provided by appointment only. This website does not constitute medical advice and is for informational purposes only.',
  },
  es: {
    eyebrow: 'La Práctica',
    headline: 'Una Práctica Basada en la Confianza',
    body: 'Pearl Ann Brown es una práctica profesional de cirugía plástica y medicina estética operada por médicos con licencia en el Estado de Nueva York. Nos especializamos en contorno corporal quirúrgico, rejuvenecimiento facial y tratamientos estéticos no quirúrgicos. Proporcionamos consultas personalizadas y confidenciales en un entorno privado, cumpliendo con todas las regulaciones estatales y federales que rigen la atención médica y la privacidad del paciente.',
    credentials: [
      {
        icon: 'shield',
        title: 'Licenciado',
        description:
          'Totalmente licenciado para practicar medicina en Nueva York',
      },
      {
        icon: 'lock',
        title: 'Confidencialidad del Paciente',
        description: 'Cumple con HIPAA con discreción completa garantizada',
      },
    ],
    statementTitle: 'Práctica Médica Profesional',
    statement:
      'Pearl Ann Brown opera como una práctica médica profesional bajo las leyes del Estado de Nueva York. Todos los médicos tienen licencia para ejercer la medicina en Nueva York. Nuestra práctica cumple con todas las regulaciones estatales y federales aplicables, incluidos los requisitos de privacidad de HIPAA. Los servicios médicos se brindan solo con cita previa. Este sitio web no constituye consejo médico y es solo para fines informativos.',
  },
} as const

export const SERVICES = {
  en: {
    eyebrow: 'Services',
    title: 'Surgical & Aesthetic Services',
    subtitle:
      'Comprehensive plastic surgery and aesthetic procedures tailored to your goals.',
    note: 'All procedures require an individual medical evaluation. Candidacy is determined during a private consultation.',
    services: [
      {
        key: 'liposuction',
        title: 'Liposuction',
        description:
          'Body contouring and fat removal for a sculpted, natural-looking result.',
      },
      {
        key: 'tummy',
        title: 'Tummy Tuck',
        description:
          'Abdominoplasty to tighten and reshape the abdominal area.',
      },
      {
        key: 'breast',
        title: 'Breast Surgery',
        description:
          'Augmentation, reduction, and lift procedures customized to you.',
      },
      {
        key: 'face',
        title: 'Facelift & Eyelid Surgery',
        description:
          'Facial rejuvenation procedures for a refreshed, youthful appearance.',
      },
      {
        key: 'injectables',
        title: 'Injectables & Fillers',
        description:
          'Non-surgical treatments including Botox and dermal fillers.',
      },
      {
        key: 'contouring',
        title: 'Body Contouring',
        description:
          'Comprehensive body sculpting including Brazilian Butt Lift (BBL).',
      },
    ],
  },
  es: {
    eyebrow: 'Servicios',
    title: 'Servicios Quirúrgicos y Estéticos',
    subtitle:
      'Cirugía plástica integral y procedimientos estéticos adaptados a sus objetivos.',
    note: 'Todos los procedimientos requieren una evaluación médica individual. La candidatura se determina durante una consulta privada.',
    services: [
      {
        key: 'liposuction',
        title: 'Liposucción',
        description:
          'Contorno corporal y eliminación de grasa para un resultado natural y esculpido.',
      },
      {
        key: 'tummy',
        title: 'Abdominoplastia',
        description: 'Cirugía para tensar y remodelar el área abdominal.',
      },
      {
        key: 'breast',
        title: 'Cirugía de Senos',
        description:
          'Aumento, reducción y levantamiento personalizados para usted.',
      },
      {
        key: 'face',
        title: 'Lifting Facial y Blefaroplastia',
        description:
          'Procedimientos de rejuvenecimiento facial para una apariencia fresca y juvenil.',
      },
      {
        key: 'injectables',
        title: 'Inyectables y Rellenos',
        description:
          'Tratamientos no quirúrgicos incluyendo Botox y rellenos dérmicos.',
      },
      {
        key: 'contouring',
        title: 'Contorno Corporal',
        description:
          'Escultura corporal integral incluyendo Levantamiento de Glúteos (BBL).',
      },
    ],
  },
} as const

export const PROCESS = {
  en: {
    eyebrow: 'How It Works',
    title: 'How It Works',
    steps: [
      {
        number: '01',
        title: 'Submit Request',
        description:
          'Fill out our confidential consultation form with your information',
      },
      {
        number: '02',
        title: 'Coordinator Contacts You',
        description:
          'Our team reaches out within 24 hours to discuss your needs',
      },
      {
        number: '03',
        title: 'Appointment Confirmed',
        description:
          'We schedule your private consultation at a convenient time',
      },
      {
        number: '04',
        title: 'Private Consultation',
        description:
          'Meet with our board-certified physician in complete privacy',
      },
    ],
  },
  es: {
    eyebrow: 'Cómo Funciona',
    title: 'Cómo Funciona',
    steps: [
      {
        number: '01',
        title: 'Enviar Solicitud',
        description:
          'Complete nuestro formulario de consulta confidencial con su información',
      },
      {
        number: '02',
        title: 'El Coordinador Se Comunica',
        description:
          'Nuestro equipo se comunica en 24 horas para discutir sus necesidades',
      },
      {
        number: '03',
        title: 'Cita Confirmada',
        description:
          'Programamos su consulta privada en un momento conveniente',
      },
      {
        number: '04',
        title: 'Consulta Privada',
        description:
          'Reúnase con nuestro médico certificado en completa privacidad',
      },
    ],
  },
} as const

export const FORM = {
  en: {
    eyebrow: 'Consultation Request',
    title: 'Request Your Private Consultation',
    subtitle:
      'Fill out the form below and our team will contact you within 24 hours',
    fields: {
      name: 'Full Name',
      phone: 'Phone Number',
      language: 'Preferred Language',
      service: 'Service of Interest',
      message: 'Message (Optional)',
    },
    select: 'Select...',
    languageOptions: ['English', 'Español'],
    serviceOptions: [
      'Liposuction',
      'Tummy Tuck',
      'Breast Surgery',
      'Facelift & Eyelid Surgery',
      'Injectables & Fillers',
      'Body Contouring / BBL',
      'General Consultation',
      'Follow-up Appointment',
      'Other',
    ],
    submit: 'Submit Request',
    disclaimer:
      'Submitting this form does not confirm an appointment. A coordinator will contact you to schedule.',
    privacyNote:
      'Do not include sensitive medical details in this form. Protected health information is collected during your private consultation.',
    trust: [
      { icon: 'clock', text: '24h Response' },
      { icon: 'shield', text: '100% Confidential' },
      { icon: 'x', text: 'No Obligation' },
    ],
    success: {
      title: 'Thank You!',
      message:
        'Thank you for your information. Our team will contact you within 24 hours to schedule your consultation.',
      again: 'Submit Another Request',
    },
  },
  es: {
    eyebrow: 'Solicitud de Consulta',
    title: 'Solicite Su Consulta Privada',
    subtitle:
      'Complete el formulario a continuación y nuestro equipo se comunicará con usted en 24 horas',
    fields: {
      name: 'Nombre Completo',
      phone: 'Número de Teléfono',
      language: 'Idioma Preferido',
      service: 'Servicio de Interés',
      message: 'Mensaje (Opcional)',
    },
    select: 'Seleccionar...',
    languageOptions: ['English', 'Español'],
    serviceOptions: [
      'Liposucción',
      'Abdominoplastia',
      'Cirugía de Senos',
      'Lifting Facial y Blefaroplastia',
      'Inyectables y Rellenos',
      'Contorno Corporal / BBL',
      'Consulta General',
      'Cita de Seguimiento',
      'Otro',
    ],
    submit: 'Enviar Solicitud',
    disclaimer:
      'Enviar este formulario no confirma una cita. Un coordinador se comunicará con usted para programar.',
    privacyNote:
      'No incluya detalles médicos sensibles en este formulario. La información médica protegida se recopila durante su consulta privada.',
    trust: [
      { icon: 'clock', text: 'Respuesta en 24h' },
      { icon: 'shield', text: '100% Confidencial' },
      { icon: 'x', text: 'Sin Obligación' },
    ],
    success: {
      title: '¡Gracias!',
      message:
        'Gracias por su información. Nuestro equipo se comunicará con usted en 24 horas para programar su consulta.',
      again: 'Enviar Otra Solicitud',
    },
  },
} as const

export const FAQ_CONTENT = {
  en: {
    eyebrow: 'Questions',
    title: 'Frequently Asked Questions',
    sub: 'If your question is not answered here, our coordinator will address it during your consultation.',
    faqs: [
      {
        question: 'How do I schedule an appointment?',
        answer:
          'Submit a consultation request through our form. Our coordinator will contact you within 24 hours to schedule your private appointment at a time that works for you.',
      },
      {
        question: 'What services do you provide?',
        answer:
          'We offer comprehensive medical consultations with board-certified physicians. Services include general medical care, specialized consultations, and follow-up appointments. All services are provided in a private, confidential setting.',
      },
      {
        question: 'Do you accept insurance?',
        answer:
          'We are a private practice and do not directly bill insurance. However, we can provide documentation for you to submit to your insurance provider for potential reimbursement.',
      },
      {
        question: 'How is my privacy protected?',
        answer:
          'We are fully HIPAA-compliant and maintain the strictest standards of patient confidentiality. All consultations are conducted in private settings, and your medical information is securely protected.',
      },
      {
        question: 'Do you offer services in Spanish?',
        answer:
          'Yes, we provide medical services in both English and Spanish. You can indicate your language preference when submitting your consultation request.',
      },
      {
        question: 'Where are you located?',
        answer:
          'We are based in New York and serve patients throughout the state. Specific location details will be provided when your appointment is confirmed.',
      },
    ],
  },
  es: {
    eyebrow: 'Preguntas',
    title: 'Preguntas Frecuentes',
    sub: 'Si su pregunta no aparece aquí, nuestro coordinador la responderá durante su consulta.',
    faqs: [
      {
        question: '¿Cómo programo una cita?',
        answer:
          'Envíe una solicitud de consulta a través de nuestro formulario. Nuestro coordinador se comunicará con usted en 24 horas para programar su cita privada en un momento que le convenga.',
      },
      {
        question: '¿Qué servicios proporcionan?',
        answer:
          'Ofrecemos consultas médicas integrales con médicos certificados. Los servicios incluyen atención médica general, consultas especializadas y citas de seguimiento. Todos los servicios se brindan en un entorno privado y confidencial.',
      },
      {
        question: '¿Aceptan seguro?',
        answer:
          'Somos una práctica privada y no facturamos directamente al seguro. Sin embargo, podemos proporcionar documentación para que la presente a su proveedor de seguros para un posible reembolso.',
      },
      {
        question: '¿Cómo se protege mi privacidad?',
        answer:
          'Cumplimos totalmente con HIPAA y mantenemos los estándares más estrictos de confidencialidad del paciente. Todas las consultas se realizan en entornos privados y su información médica está protegida de forma segura.',
      },
      {
        question: '¿Ofrecen servicios en español?',
        answer:
          'Sí, proporcionamos servicios médicos tanto en inglés como en español. Puede indicar su preferencia de idioma al enviar su solicitud de consulta.',
      },
      {
        question: '¿Dónde están ubicados?',
        answer:
          'Estamos ubicados en Nueva York y atendemos a pacientes en todo el estado. Los detalles específicos de la ubicación se proporcionarán cuando se confirme su cita.',
      },
    ],
  },
} as const

export const CONTACT = {
  en: {
    eyebrow: 'Contact',
    title: 'Contact & Location',
    sub: 'Pearl Ann Brown is a private practice. Consultations are scheduled by appointment only.',
    labels: {
      address: 'Address',
      email: 'Email',
      hours: 'Hours',
      languages: 'Languages',
    },
    hours: 'By Appointment Only',
    languages: 'English & Spanish',
  },
  es: {
    eyebrow: 'Contacto',
    title: 'Contacto y Ubicación',
    sub: 'Pearl Ann Brown es una práctica privada. Las consultas se programan solo con cita previa.',
    labels: {
      address: 'Dirección',
      email: 'Email',
      hours: 'Horario',
      languages: 'Idiomas',
    },
    hours: 'Solo con Cita Previa',
    languages: 'Inglés y Español',
  },
} as const

export const FOOTER = {
  en: {
    legal:
      'Professional medical practice. Licensed physicians in New York State.',
    copyright: '© 2026 Pearl Ann Brown. All rights reserved.',
    contact: 'Contact',
    hours: 'Hours',
    hoursValue: 'By Appointment Only',
    navigate: 'Navigate',
    legalLinks: 'Legal',
    disclaimer:
      'This website does not constitute medical advice and is for informational purposes only. Medical services are provided by appointment only. Individual results vary and are determined by an individual medical evaluation.',
    links: {
      privacy: 'Privacy Policy',
      terms: 'Terms of Use',
      disclaimer: 'Medical Disclaimer',
      accessibility: 'Accessibility',
    },
  },
  es: {
    legal:
      'Práctica médica profesional. Médicos con licencia en el Estado de Nueva York.',
    copyright: '© 2026 Pearl Ann Brown. Todos los derechos reservados.',
    contact: 'Contacto',
    hours: 'Horario',
    hoursValue: 'Solo con Cita Previa',
    navigate: 'Navegación',
    legalLinks: 'Legal',
    disclaimer:
      'Este sitio web no constituye consejo médico y es solo para fines informativos. Los servicios médicos se brindan solo con cita previa. Los resultados individuales varían y se determinan mediante una evaluación médica individual.',
    links: {
      privacy: 'Política de Privacidad',
      terms: 'Términos de Uso',
      disclaimer: 'Aviso Médico',
      accessibility: 'Accesibilidad',
    },
  },
} as const
